// Session management utilities for authentication
import { API_BASE_URL } from '../services/ApiUrls';

/** Error name set by BaseApiService when the session is cleared after 401. */
export const AUTH_SESSION_ERROR = 'AuthSessionError';

let authInvalidationInProgress = false;

export function isLoggedIn(): boolean {
  const token = localStorage.getItem('token');
  if (!token) return false;
  const trimmed = token.trim();
  return trimmed.length > 0 && trimmed !== 'null' && trimmed !== 'undefined';
}



export function getUser() {
  const raw = localStorage.getItem('user');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Normalized role from session user (e.g. STUDENT, ORGANIZATION). */
export function getUserRole(): string | null {
  const user = getUser();
  if (!user) return null;

  const rawRole = user.role ?? user.roleName ?? user.userRole;
  if (rawRole != null && rawRole !== '') {
    if (typeof rawRole === 'object' && rawRole.roleName != null) {
      return String(rawRole.roleName).toUpperCase();
    }
    return String(rawRole).toUpperCase();
  }

  const roleId = user.roleId ?? user.role_id;
  if (roleId === 1 || roleId === '1') return 'STUDENT';
  if (roleId === 2 || roleId === '2') return 'ORGANIZATION';
  if (roleId === 3 || roleId === '3') return 'MENTOR';
  if (roleId === 4 || roleId === '4') return 'SPONSOR';

  return null;
}

export function isStudentUser(): boolean {
  return getUserRole() === 'STUDENT';
}

/** Public student/user id from login session (camelCase or snake_case from API). */
export function getUserUniqueId(): string | null {
  const user = getUser();
  if (!user) return null;
  const raw =
    user.userUniqueId ??
    user.user_unique_id ??
    user.userUniqId ??
    user.UserUniqId;
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).trim();
}

/**
 * Whether the Apply button is shown on public jobs/internships pages.
 * - Not logged in: always show (opens student-only login on click).
 * - Logged in: only for STUDENT role.
 */
export function shouldShowApplyButton(): boolean {
  if (!isLoggedIn()) return true;
  return isStudentUser();
}

/** @alias {@link shouldShowApplyButton} */
export function canStudentApply(): boolean {
  return shouldShowApplyButton();
}

/** @deprecated Prefer {@link canStudentApply} — same behavior. */
export function canApplyToJobs(): boolean {
  return canStudentApply();
}

export function getToken(): string | null {
  return localStorage.getItem('token');
}

const SESSION_ESTABLISHED_KEY = 'sessionEstablishedAt';

/** Grace period after login — ignore 401s from stale in-flight requests. */
export const POST_LOGIN_AUTH_GRACE_MS = 8000;

export function isAuthSessionError(error: unknown): boolean {
  if (!error) return false;
  if (error instanceof Error) {
    if (error.name === AUTH_SESSION_ERROR) return true;
    const msg = error.message || '';
    return (
      msg.includes('Session expired') ||
      msg.includes('No authentication token') ||
      msg.includes('User account not found')
    );
  }
  return false;
}

/**
 * Clear stale JWT/user from storage once and optionally redirect to login.
 * Prevents dozens of duplicate "Session expired" toasts on dashboard load.
 */
export function invalidateAuthSession(reason?: string): void {
  if (authInvalidationInProgress) return;
  const hadToken = Boolean(localStorage.getItem('token'));
  if (!hadToken) return;

  authInvalidationInProgress = true;
  clearSession();

  void import('antd').then(({ message: antMessage }) => {
    antMessage.error(
      reason ||
        'Your session is no longer valid. Please sign in again.',
      4
    );
  });

  if (shouldRedirectToLoginAfterAuthFailure()) {
    window.setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }

  window.setTimeout(() => {
    authInvalidationInProgress = false;
  }, 3000);
}

export function setSession(
  token: string,
  user?: any,
  refreshToken?: string,
  subscription?: Record<string, any> | null,
  features?: Record<string, any> | null,
  subscriptionStartDate?: string | null,
  subscriptionExpiryDate?: string | null
) {
  authInvalidationInProgress = false;
  localStorage.setItem('token', token);
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  }
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
  if (subscription) {
    localStorage.setItem('subscription', JSON.stringify(subscription));
  }
  if (features) {
    localStorage.setItem('features', JSON.stringify(features));
  }
  if (subscriptionStartDate) {
    localStorage.setItem('subscriptionStartDate', subscriptionStartDate);
  } else {
    localStorage.removeItem('subscriptionStartDate');
  }
  if (subscriptionExpiryDate) {
    localStorage.setItem('subscriptionExpiryDate', subscriptionExpiryDate);
  } else {
    localStorage.removeItem('subscriptionExpiryDate');
  }
  sessionStorage.setItem(SESSION_ESTABLISHED_KEY, String(Date.now()));
  window.dispatchEvent(new Event('authChange'));
}

export function isWithinPostLoginGracePeriod(): boolean {
  const established = Number(sessionStorage.getItem(SESSION_ESTABLISHED_KEY) || 0);
  if (!established) {
    return false;
  }
  return Date.now() - established < POST_LOGIN_AUTH_GRACE_MS;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken');
}

export function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('subscription');
  localStorage.removeItem('features');
  localStorage.removeItem('subscriptionStartDate');
  localStorage.removeItem('subscriptionExpiryDate');
  localStorage.removeItem('registrationStatus');
  localStorage.removeItem('subscriptionNearingExpiry');
  localStorage.removeItem('subscriptionGatingEnabled');
  localStorage.removeItem('hasActiveSubscription');
  sessionStorage.removeItem(SESSION_ESTABLISHED_KEY);
  sessionStorage.removeItem('postLoginPromptsShown');
  window.dispatchEvent(new Event('authChange'));
}

/** PENDING / PAYMENTPENDING / APPROVED / AUTO_APPROVED / REJECTED — set at login/registration. */
export function setRegistrationStatus(status: string | null | undefined) {
  if (status) {
    localStorage.setItem('registrationStatus', status);
  } else {
    localStorage.removeItem('registrationStatus');
  }
}

export function getRegistrationStatus(): string | null {
  return localStorage.getItem('registrationStatus');
}

/** Role has subscription plans configured but the user hasn't chosen/paid for one yet. */
export function isPaymentPendingRegistration(): boolean {
  return getRegistrationStatus() === 'PAYMENTPENDING';
}

/** Server-computed: active subscription is within the renewal reminder window. */
export function setSubscriptionNearingExpiry(nearing: boolean) {
  localStorage.setItem('subscriptionNearingExpiry', nearing ? '1' : '0');
}

export function isSubscriptionNearingExpiry(): boolean {
  return localStorage.getItem('subscriptionNearingExpiry') === '1';
}

/**
 * True when the login response included the subscription-selection field at all — this
 * only happens when the user's role has subscription plans configured. Used to scope
 * SubscriptionGuard checks so roles without plans are never gated.
 */
export function setSubscriptionGatingEnabled(enabled: boolean) {
  localStorage.setItem('subscriptionGatingEnabled', enabled ? '1' : '0');
}

export function isSubscriptionGatingEnabled(): boolean {
  return localStorage.getItem('subscriptionGatingEnabled') === '1';
}

/**
 * Authoritative "does the user currently have an active subscription" flag, computed
 * server-side at login (inverse of requiresSubscriptionSelection) and at payment success.
 * This is the source of truth for SubscriptionGuard — unlike the legacy `subscription`
 * object below, it is always set on login regardless of whether the full subscription
 * payload is present in the response.
 */
export function setHasActiveSubscription(active: boolean) {
  localStorage.setItem('hasActiveSubscription', active ? '1' : '0');
}

export function getHasActiveSubscriptionFlag(): boolean | null {
  const v = localStorage.getItem('hasActiveSubscription');
  if (v === null) return null;
  return v === '1';
}

export function getSubscription(): Record<string, any> | null {
  const raw = localStorage.getItem('subscription');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getFeatures(): Record<string, any> {
  const raw = localStorage.getItem('features');
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function getSubscriptionStartDate(): string | null {
  return localStorage.getItem('subscriptionStartDate');
}

export function getSubscriptionExpiryDate(): string | null {
  return localStorage.getItem('subscriptionExpiryDate');
}

/**
 * Returns true if the user has no subscription or the subscription has expired.
 * Prefers the server-computed `hasActiveSubscription` flag (set on every login and
 * payment success) over the legacy `subscription` object, which is only populated
 * by the payment flow and is never present on a plain login response.
 */
export function isSubscriptionExpired(): boolean {
  const activeFlag = getHasActiveSubscriptionFlag();
  const expiry = getSubscriptionExpiryDate();

  if (activeFlag !== null) {
    if (!activeFlag) return true;
    if (!expiry) return false;
    const expiryDate = new Date(expiry);
    if (isNaN(expiryDate.getTime())) return false;
    return new Date() > expiryDate;
  }

  // Legacy fallback for sessions established before this flag existed.
  const sub = getSubscription();
  if (!sub) return true;
  if (!expiry) return false;
  const expiryDate = new Date(expiry);
  if (isNaN(expiryDate.getTime())) return false;
  return new Date() > expiryDate;
}

/**
 * Returns true when the given feature key is enabled for the current user's subscription.
 * Always reads from localStorage — no API call required.
 *
 * Usage:
 *   if (hasFeature('CREATE_JOB_POST')) { ... }
 */
export function hasFeature(featureKey: string): boolean {
  const features = getFeatures();
  const value = features[featureKey];
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    return lower === 'true' || (lower !== 'false' && lower !== '0' && lower !== '');
  }
  return Boolean(value);
}

/**
 * When an authenticated API returns 401 we clear the session. A full hard redirect to /login is only
 * needed on protected areas. On public routes (e.g. /login, /) a stale token can cause parallel 401s
 * while the user is submitting invalid credentials — redirect would interrupt the form.
 */
export function shouldRedirectToLoginAfterAuthFailure(): boolean {
  if (typeof window === 'undefined') return true;
  let path = window.location.pathname || '/';
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  const publicExact = new Set([
    '/',
    '/home',
    '/login',
    '/register',
    '/internships',
    '/jobs',
    '/mentorships',
    '/scholarships',
    '/events',
    '/community',
    '/remote-hub',
    '/about-us',
    '/our-partners',
    '/accolades',
    '/contact-us',
    '/test-api',
    '/encryption-test',
  ]);
  if (publicExact.has(path)) return false;

  if (path.startsWith('/internships/')) return false;

  if (path.startsWith('/programs/')) return false;

  return true;
}

export function logout() {
  // The Learning Hub's CloudFront cookies are a separate, HttpOnly credential with their own
  // ~8h lifetime that only CloudFront checks — clearing our own session doesn't touch them, so
  // without this a "logged out" user could still reach the Learning Hub until those expire.
  // Fire-and-forget: logout must not block or fail because this network call is slow/down.
  // keepalive lets the request survive the navigation below instead of being aborted mid-flight.
  fetch(`${API_BASE_URL}/learninghub/revoke`, { method: 'POST', credentials: 'include', keepalive: true }).catch(() => {});
  clearSession();
  window.location.href = '/';
}

// Utility function to manually trigger logout modal for testing
export function triggerLogoutModal(options?: {
  title?: string;
  message?: string;
  showRetryButton?: boolean;
  onRetry?: () => void;
}) {
  window.dispatchEvent(new CustomEvent('showLogoutModal', {
    detail: options || {
      title: 'Session Expired',
      message: 'Your session has expired due to an authentication error. Please log in again to continue.',
      showRetryButton: false
    }
  }));
} 